import { Check, ChevronDown, LogOut, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { roleLabel } from '../auth/roleOptions'
import type { EntityKind, PersonaSummary } from '../types/api'

/** The order roles are listed in, so the menu reads the same every time. */
const ORDER: EntityKind[] = ['STUDENT', 'TUTOR', 'GUARDIAN', 'CENTRE', 'COUNSELLOR', 'SCHOOL', 'COLLEGE', 'COMPANY']

/**
 * "Choose your avatar" - the header dropdown that switches between the roles this person already
 * holds: exactly those, however many (one, three, six), and nothing else. A new role is not added
 * from here; it is added by signing up with the same email for that role.
 */
export function AvatarSwitcher() {
  const { profile, roles, switchProfile, logout } = useAuth()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The menu covers whatever is under it, so it must close itself rather than wait to be told.
  useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (!profile) return null

  function closeMenu() {
    setOpen(false)
    setError(null)
  }

  async function onSwitch(personaId: string) {
    if (busy || personaId === profile!.id) return
    setBusy(true)
    setError(null)
    try {
      await switchProfile(personaId)
      closeMenu()
      // Home rather than wherever they were - half the routes are persona-specific, so staying
      // put would land some switches on a page that no longer applies to the new role.
      navigate('/profile', { replace: true })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not switch profile'))
    } finally {
      setBusy(false)
    }
  }

  // Every role held, in a fixed order. If the roles list could not be loaded, the one being used
  // now is still shown, so the menu never claims they hold nothing.
  const held: PersonaSummary[] = (roles.length > 0
    ? [...roles]
    : [{ personaId: profile.id, entityKind: profile.entityKind } as PersonaSummary]
  )
    // One role of a kind per person: should the list ever carry the same kind twice, keep the one in
    // use, else the first.
    .filter((r, _i, all) => {
      const sameKind = all.filter((o) => o.entityKind === r.entityKind)
      return (sameKind.find((o) => o.personaId === profile.id) ?? sameKind[0]) === r
    })
    .sort((a, b) => ORDER.indexOf(a.entityKind) - ORDER.indexOf(b.entityKind))

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => (open ? closeMenu() : setOpen(true))}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Choose your avatar"
        className="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2 text-sm leading-tight transition hover:bg-ink-100"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-100 text-ink-600">
          <User size={15} />
        </span>
        <span className="hidden text-left sm:block">
          <span className="block max-w-[12rem] truncate font-semibold text-ink-900">{profile.fullName}</span>
          <span className="block text-[10px] uppercase tracking-wide text-ink-400">
            {roleLabel(profile.entityKind)}
          </span>
        </span>
        <ChevronDown size={14} className={`shrink-0 text-ink-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-ink-200 bg-white p-2 shadow-float"
        >
          <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
            {held.length > 1 ? 'Switch role' : 'Your role'}
          </div>

          {held.map((role) => {
            const isCurrent = role.personaId === profile.id
            return (
              <button
                key={role.personaId}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                disabled={isCurrent || busy}
                onClick={() => onSwitch(role.personaId)}
                className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm font-medium text-ink-700 transition hover:bg-ink-100 disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span>{roleLabel(role.entityKind)}</span>
                {isCurrent && <Check size={15} className="shrink-0 text-emerald-600" />}
              </button>
            )
          })}

          {error && <p className="mt-1 px-2 text-xs text-red-600">{error}</p>}

          <div className="mt-1 border-t border-ink-100 pt-1">
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-ink-600 transition hover:bg-ink-100"
            >
              <LogOut size={15} className="shrink-0 text-ink-400" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
